import { call, put, takeLatest, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse, ApiError } from "@/lib/apiClient";
import {
  getUnifiedDashboard,
  getDashboardPanel,
} from "@/services/dashboardService";
import {
  fetchDashboardRequest,
  fetchDashboardSuccess,
  fetchDashboardFailure,
  refreshPanelRequest,
  refreshPanelSuccess,
  refreshPanelFailure,
} from "@/store/slices/dashboard.slice";

function* fetchDashboardSaga(): Generator<any, void, any> {
  try {
    // Try to get storeId from auth slice
    const authState = yield select((state: any) => state.auth);
    const storeId =
      authState?.currentStoreId || authState?.currentStoreInfo?.storeId;
    if (!storeId) {
      // If storeId is not set, fail gracefully
      yield put(
        fetchDashboardFailure(
          new ApiError(400, "NO_STORE_SELECTED", "No store selected"),
        ),
      );
      return;
    }

    // Fetch unified dashboard data
    const response: ApiResponse<any> = yield call(getUnifiedDashboard, storeId);
    const apiData = response.data ?? response;

    // Map monthly_data to chart format with month names
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const chartData =
      apiData.earnings_report?.monthly_data?.map((item: any) => ({
        name: monthNames[item.month - 1],
        orders: item.orders || 0,
        wholesalers: item.sales || 0,
        profit: item.profit || 0,
        income: item.income || 0,
        expenses: item.expenses || 0,
      })) || [];

    // Extract different panel data from unified response
    const dashboardData = {
      stats: apiData.statistics || null,
      chartData,
      analytics: apiData.site_analytics || null,
      transactions: apiData.transactions?.transactions || [],
      topProducts: apiData.top_products || [],
      activeOrders: Array.isArray(apiData.active_orders?.orders)
        ? apiData.active_orders.orders
        : Array.isArray(apiData.active_orders)
          ? apiData.active_orders
          : [],
      customers: apiData.customer_list?.customers || [],
      campaignData: apiData.campaign_data || null,
      salesByState: apiData.sales_by_state || null,
      salesByCountries: apiData.sales_by_countries || null,
      stockAnalysis: apiData.stock_analysis || null,
      trafficSources: apiData.traffic_sources || null,
      snapshot: apiData,
    };

    yield put(fetchDashboardSuccess(dashboardData));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchDashboardFailure(error));
    } else {
      yield put(
        fetchDashboardFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

function* refreshPanelSaga(
  action: PayloadAction<{ panel: string }>,
): Generator<any, void, any> {
  try {
    const { panel } = action.payload;

    // Try to get storeId from auth slice
    const authState = yield select((state: any) => state.auth);
    const storeId =
      authState?.currentStoreId || authState?.currentStoreInfo?.storeId;
    if (!storeId) {
      yield put(
        refreshPanelFailure({
          panel,
          error: new ApiError(400, "NO_STORE_SELECTED", "No store selected"),
        }),
      );
      return;
    }

    // Fetch specific panel data
    const response: ApiResponse<any> = yield call(
      getDashboardPanel,
      storeId,
      panel,
    );
    const apiData = response.data ?? response;

    // Map the response based on panel type
    let data;
    switch (panel) {
      case "statistics":
        data = apiData.statistics || apiData;
        break;
      case "earnings":
        {
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          data =
            apiData.earnings_report?.monthly_data?.map((item: any) => ({
              name: monthNames[item.month - 1],
              orders: item.orders || 0,
              wholesalers: item.sales || 0,
              profit: item.profit || 0,
              income: item.income || 0,
              expenses: item.expenses || 0,
            })) || apiData;
        }
        break;
      case "analytics":
        data = apiData.site_analytics || apiData;
        break;
      case "transactions":
        data = Array.isArray(apiData.transactions?.transactions)
          ? apiData.transactions.transactions
          : Array.isArray(apiData.transactions)
            ? apiData.transactions
            : Array.isArray(apiData)
              ? apiData
              : [];
        break;
      case "topProducts":
        data = Array.isArray(apiData.top_products)
          ? apiData.top_products
          : Array.isArray(apiData)
            ? apiData
            : [];
        break;
      case "activeOrders":
        data = Array.isArray(apiData.active_orders?.orders)
          ? apiData.active_orders.orders
          : Array.isArray(apiData.active_orders)
            ? apiData.active_orders
            : Array.isArray(apiData.orders)
              ? apiData.orders
              : Array.isArray(apiData)
                ? apiData
                : [];
        break;
      case "customers":
        data = Array.isArray(apiData.customer_list?.customers)
          ? apiData.customer_list.customers
          : Array.isArray(apiData.customers)
            ? apiData.customers
            : Array.isArray(apiData)
              ? apiData
              : [];
        break;
      case "campaignData":
        data = apiData.campaign_data || apiData;
        break;
      case "salesByState":
        data = apiData.sales_by_state || apiData;
        break;
      case "salesByCountries":
        data = apiData.sales_by_countries || apiData;
        break;
      case "stockAnalysis":
        data = apiData.stock_analysis || apiData;
        break;
      case "trafficSources":
        data = apiData.traffic_sources || apiData;
        break;
      default:
        data = apiData;
    }

    yield put(refreshPanelSuccess({ panel, data }));
  } catch (error: any) {
    const { panel } = action.payload;
    if (error instanceof ApiError) {
      yield put(refreshPanelFailure({ panel, error }));
    } else {
      yield put(
        refreshPanelFailure({
          panel,
          error: new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        }),
      );
    }
  }
}

export default function* dashboardRootSaga(): Generator<any, void, any> {
  yield takeLatest(fetchDashboardRequest.type, fetchDashboardSaga);
  yield takeLatest(refreshPanelRequest.type, refreshPanelSaga);
}
